import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';

import { ButtonDropdown } from '@/components/core/ButtonDropdown/ButtonDropdown';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuPortal,
    DropdownMenuTrigger,
} from '@/components/core/DropdownMenu/DropdownMenu';
import { Icon } from '@/components/core/Icon';
import { useTranslation } from '@/hooks/useTranslation';

import { FilterMenuItem } from '../FilterDropdown/FilterMenuItem';
import { BulkSearch, BulkSearchDialogState } from './bulkSearch.types';
import { BulkSearchDialog } from './BulkSearchDialog';
import { readStoredBulkSearch } from './bulkSearchSession';

export interface BulkSearchDropdownProps {
    onApplyBulkSearch: (bulkSearch: BulkSearch) => void;
    /** Optional seed when opened from SEARCH_QUERY_TOO_LONG. */
    dialogState?: BulkSearchDialogState | null;
    onDialogStateConsumed?: () => void;
    category?: string | null;
}

export function BulkSearchDropdown({
    onApplyBulkSearch,
    dialogState,
    onDialogStateConsumed,
    category,
}: BulkSearchDropdownProps) {
    const { t } = useTranslation();
    const [menuOpen, setMenuOpen] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [initialDialogState, setInitialDialogState] = useState<BulkSearchDialogState>({});
    const [lastBulkSearch, setLastBulkSearch] = useState(readStoredBulkSearch);

    const openNewDialog = (state: BulkSearchDialogState = {}) => {
        setInitialDialogState(state);
        setDialogOpen(true);
    };

    useEffect(() => {
        if (!dialogState) {
            return;
        }
        openNewDialog(dialogState);
        onDialogStateConsumed?.();
    }, [dialogState, onDialogStateConsumed]);

    const handleShowLastResults = () => {
        const stored = lastBulkSearch ?? readStoredBulkSearch();
        if (!stored?.id) {
            return;
        }
        onApplyBulkSearch(stored);
    };

    return (
        <>
            <DropdownMenu
                open={menuOpen}
                onOpenChange={(open) => {
                    setMenuOpen(open);
                    if (open) {
                        setLastBulkSearch(readStoredBulkSearch());
                    }
                }}
            >
                <DropdownMenuTrigger asChild>
                    <ButtonDropdown
                        titleOne={t('BULK_SEARCH')}
                        isOpen={menuOpen}
                        aria-label={t('BULK_SEARCH')}
                    />
                </DropdownMenuTrigger>
                <DropdownMenuPortal>
                    <DropdownMenuContent
                        hasBorder
                        hasPadding={false}
                        align="start"
                        className="tw-min-w-[12rem]"
                    >
                        <FilterMenuItem onSelect={() => openNewDialog()}>
                            <span className="tw-inline-flex tw-items-center tw-gap-1.5">
                                <Icon icon={PlusIcon} size="xs" variant="secondary" />
                                {t('BULK_SEARCH.NEW')}
                            </span>
                        </FilterMenuItem>
                        {lastBulkSearch && (
                            <FilterMenuItem onSelect={handleShowLastResults}>
                                <span className="tw-inline-flex tw-items-center tw-gap-1.5">
                                    <Icon
                                        icon={MagnifyingGlassIcon}
                                        size="xs"
                                        variant="secondary"
                                    />
                                    {t('BULK_SEARCH.RESULTS')}
                                </span>
                            </FilterMenuItem>
                        )}
                    </DropdownMenuContent>
                </DropdownMenuPortal>
            </DropdownMenu>
            {dialogOpen && (
                <BulkSearchDialog
                    key={`${initialDialogState.queryTooLong ?? 0}-${initialDialogState.queryValues ?? ''}`}
                    open={dialogOpen}
                    initialState={initialDialogState}
                    category={category}
                    onClose={() => setDialogOpen(false)}
                    onStarted={(bulkSearch) => {
                        setLastBulkSearch(bulkSearch);
                        onApplyBulkSearch(bulkSearch);
                    }}
                />
            )}
        </>
    );
}
